import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, FileText, FileDown, BarChart3 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const statusMap = {
  paid: { label: "Pago", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  pending: { label: "Pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  cancelled: { label: "Cancelado", cls: "bg-stone-200 text-stone-700 border-stone-300" },
};

export default function ManageReports() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [size, setSize] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch {
      toast.error("Erro ao carregar pedidos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const sizeLow = size.toLowerCase();
    return orders.filter((o) => {
      if (status !== "all" && o.status !== status) return false;
      if (source !== "all" && (o.source || "order") !== source) return false;
      if (dateFrom && o.created_at < dateFrom) return false;
      if (dateTo && o.created_at > dateTo + "T23:59:59.999") return false;
      if (size !== "all") {
        const hasSize = (o.items || []).some((it) => (it.size || "").toLowerCase() === sizeLow);
        if (!hasSize) return false;
      }
      return true;
    });
  }, [orders, status, source, size, dateFrom, dateTo]);

  const totals = useMemo(() => {
    const paid = filtered.filter((o) => o.status === "paid").reduce((s, o) => s + o.total, 0);
    const pending = filtered.filter((o) => o.status === "pending").reduce((s, o) => s + o.total, 0);
    return { paid, pending };
  }, [filtered]);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (source !== "all") params.set("source", source);
      if (size !== "all") params.set("size", size);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo + "T23:59:59.999");
      const { data, headers } = await api.get(`/reports/orders.${fmt}?${params.toString()}`, {
        responseType: "blob",
      });
      const cd = headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match ? match[1] : `relatorio-vendas.${fmt}`;
      const blob = new Blob([data], { type: data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Relatório exportado");
    } catch {
      toast.error("Erro ao exportar relatório");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="manage-reports">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Relatórios</div>
        <h1 className="font-display text-4xl font-bold mt-2">Relatório de vendas</h1>
        <p className="text-stone-600 mt-2">
          Filtre os pedidos e exporte a planilha ou PDF com os dados do comprador, itens e totais.
        </p>
      </div>

      <Card className="border-stone-200 rounded-2xl">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="report-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="paid">Pagos</SelectItem>
                  <SelectItem value="cancelled">Cancelados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Origem</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="mt-1" data-testid="report-source-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="subscription">Somente inscrições</SelectItem>
                  <SelectItem value="order">Somente pedidos extras</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tamanho</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger className="mt-1" data-testid="report-size-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="PP">PP</SelectItem>
                  <SelectItem value="P">P</SelectItem>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="G">G</SelectItem>
                  <SelectItem value="GG">GG</SelectItem>
                  <SelectItem value="XG">XG</SelectItem>
                  <SelectItem value="XGG">XGG</SelectItem>
                  <SelectItem value="EXG">EXG</SelectItem>
                  <SelectItem value="EXGG">EXGG</SelectItem>
                  <SelectItem value="1 ano">1 Ano</SelectItem>
                  <SelectItem value="2 anos">2 Anos</SelectItem>
                  <SelectItem value="4 anos">4 Anos</SelectItem>
                  <SelectItem value="6 anos">6 Anos</SelectItem>
                  <SelectItem value="8 anos">8 Anos</SelectItem>
                  <SelectItem value="10 anos">10 Anos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="from">De (data)</Label>
              <Input
                id="from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1"
                data-testid="report-date-from"
              />
            </div>
            <div>
              <Label htmlFor="to">Até</Label>
              <Input
                id="to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1"
                data-testid="report-date-to"
              />
            </div>
            <div className="flex items-end">
              <div className="w-full text-right">
                <div className="text-xs uppercase tracking-widest text-stone-500">Filtrados</div>
                <div className="font-display text-2xl font-bold">{filtered.length}</div>
                <div className="text-xs text-stone-500">pedido(s)</div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-stone-500">Pagos</div>
                <div className="font-semibold text-emerald-700 text-lg" data-testid="report-total-paid">
                  {formatBRL(totals.paid)}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-stone-500">Pendentes</div>
                <div className="font-semibold text-amber-700 text-lg" data-testid="report-total-pending">
                  {formatBRL(totals.pending)}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => doExport("csv")}
                disabled={!!exporting || filtered.length === 0}
                variant="outline"
                className="rounded-full"
                data-testid="export-csv-btn"
              >
                {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
                CSV
              </Button>
              <Button
                onClick={() => doExport("xlsx")}
                disabled={!!exporting || filtered.length === 0}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="export-xlsx-btn"
              >
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                Excel (.xlsx)
              </Button>
              <Button
                onClick={() => doExport("pdf")}
                disabled={!!exporting || filtered.length === 0}
                className="rounded-full bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white"
                data-testid="export-pdf-btn"
              >
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-stone-200 rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-stone-200 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-orange-600" />
          <div className="font-display text-xl font-semibold">Prévia dos pedidos</div>
        </div>
        {loading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-stone-500 text-sm">
            Nenhum pedido encontrado no filtro atual.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-stone-600 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-6 py-3">Data</th>
                  <th className="text-left px-6 py-3">Cliente</th>
                  <th className="text-left px-6 py-3">Contato</th>
                  <th className="text-left px-6 py-3">Itens</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-right px-6 py-3">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-testid="report-preview-body">
                {filtered.map((o) => {
                  const st = statusMap[o.status] || statusMap.pending;
                  return (
                    <tr key={o.id} data-testid={`report-row-${o.id}`}>
                      <td className="px-6 py-3 whitespace-nowrap text-stone-600">
                        {new Date(o.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-6 py-3 font-medium text-stone-900">{o.customer_name}</td>
                      <td className="px-6 py-3 text-stone-600">
                        <div>{o.customer_email}</div>
                        <div className="text-xs text-stone-500">{o.customer_phone}</div>
                      </td>
                      <td className="px-6 py-3 text-stone-700">
                        <ul className="space-y-0.5">
                          {(o.items || []).map((it, idx) => (
                            <li key={`${o.id}-${idx}-${it.item_id || it.name}`}>
                              <strong>{it.quantity}×</strong> {it.name}
                              {(it.size || it.variant) && (
                                <span className="text-xs text-stone-500 ml-1">
                                  [{[it.size, it.variant].filter(Boolean).join(" · ")}]
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-3">
                        <Badge className={`${st.cls} hover:${st.cls}`}>{st.label}</Badge>
                      </td>
                      <td className="px-6 py-3 text-right font-semibold whitespace-nowrap">{formatBRL(o.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
